import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Activity, Inbox, Send, Gift, BookOpen, MessageSquare, ArrowLeftRight, Bell, Sparkles } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { getMyActivity } from '@/lib/api';

const ICONS = {
  incoming_request: [Inbox, 'text-red-500 bg-red-500/10'],
  request_status: [Send, 'text-sky-500 bg-sky-500/10'],
  offer_received: [Gift, 'text-purple-500 bg-purple-500/10'],
  offer_status: [BookOpen, 'text-fuchsia-500 bg-fuchsia-500/10'],
  message: [MessageSquare, 'text-blue-500 bg-blue-500/10'],
  transaction: [ArrowLeftRight, 'text-emerald-500 bg-emerald-500/10'],
  notification: [Sparkles, 'text-indigo-500 bg-indigo-500/10'],
};

const renderBold = (text) => text.split(/(<b>.*?<\/b>)/g).map((part, i) =>
  part.startsWith('<b>') ? <b key={i} className="font-semibold text-foreground">{part.slice(3, -4)}</b> : part);

const timeAgo = (iso, t) => {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return t('hub.feed.justNow', 'gerade eben');
  if (diff < 3600) return `${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h`;
  if (diff < 7 * 86400) return `${Math.floor(diff / 86400)} d`;
  return new Date(iso).toLocaleDateString();
};

const fmtPrice = (p) => (p == null ? '' : Number(p).toLocaleString('de-DE', { minimumFractionDigits: 2 }));

export const HubActivityFeed = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getMyActivity(20).then((r) => setData(r.data)).catch(() => setData({ items: [], new_count: 0 })).finally(() => setLoading(false));
  }, []);

  const label = (item) => {
    const d = item.data || {};
    const status = d.status ? t(`status.${d.status}`, d.status) : '';
    const role = d.role === 'seller' ? t('hub.asSeller') : t('hub.asBuyer');
    if (item.type === 'notification') return `${d.title}${d.message ? ` – ${d.message}` : ''}`;
    return t(`hub.feed.${item.type}`, { ...d, status, role, price: fmtPrice(d.price), order: d.order || '', preview: (d.preview || '').slice(0, 80) });
  };

  return (
    <Card data-testid="hub-activity-feed">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base md:text-lg">
            <Activity className="h-4 w-4 text-orange-500" /> {t('hub.feed.title')}
          </CardTitle>
          {data && (data.new_count > 0 ? (
            <Badge className="bg-orange-500 text-white" data-testid="hub-feed-new-count">{t('hub.feed.newCount', { count: data.new_count })}</Badge>
          ) : data.last_seen_at ? (
            <span className="text-xs text-muted-foreground" data-testid="hub-feed-caught-up">{t('hub.feed.allCaughtUp')}</span>
          ) : null)}
        </div>
        <CardDescription>{t('hub.feed.desc')}</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
        ) : !data?.items?.length ? (
          <p className="text-sm text-muted-foreground py-4" data-testid="hub-feed-empty">{t('hub.feed.empty')}</p>
        ) : (
          <ol className="relative border-l border-border/70 ml-3 space-y-1" data-testid="hub-feed-list">
            {data.items.map((item) => {
              const [Icon, cls] = ICONS[item.type] || [Bell, 'text-muted-foreground bg-muted'];
              return (
                <li key={item.id} className="relative pl-6 py-1.5" data-testid={`hub-feed-item-${item.type}`} data-new={item.is_new ? 'true' : 'false'}>
                  <span className={`absolute -left-[13px] top-2 inline-flex h-6 w-6 items-center justify-center rounded-full ring-4 ring-background ${cls}`}>
                    <Icon className="h-3 w-3" />
                  </span>
                  <Link to={item.to} className={`block rounded-md px-2 py-1.5 -mx-2 text-sm transition-colors hover:bg-muted/60 ${item.is_new ? 'bg-orange-500/5' : ''}`}>
                    <span className="text-muted-foreground">{renderBold(label(item))}</span>
                    <span className="flex items-center gap-2 mt-0.5 text-[11px] text-muted-foreground/70">
                      {timeAgo(item.ts, t)}
                      {item.is_new && <Badge className="bg-orange-500/15 text-orange-600 dark:text-orange-400 text-[10px] px-1.5 py-0" data-testid="hub-feed-new-badge">{t('hub.feed.new')}</Badge>}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ol>
        )}
      </CardContent>
    </Card>
  );
};
